export function formatDate(date: string | Date): string {
  if (typeof date === 'string') {
    const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${month}/${day}/${year}`;
    }

    const dashMatch = date.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashMatch) {
      const [, month, day, year] = dashMatch;
      return `${month}/${day}/${year}`;
    }

    const slashMatch = date.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) {
      const [, month, day, year] = slashMatch;
      return `${month}/${day}/${year}`;
    }
  }

  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  const year = String(d.getFullYear());
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${month}/${day}/${year}`;
}

export function formatDateToYmd(
  date: string | Date | null | undefined,
): string | undefined {
  const parts = getDateOnlyParts(date);
  if (!parts) return undefined;

  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function parseLocalDate(value: string | Date): Date {
  if (value instanceof Date) return value;

  const normalized = value.trim();
  const ymdMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  return new Date(normalized);
}

export function formatDateFromYmd(date: string | Date): string {
  let year: number;
  let month: string;
  let day: string;

  if (typeof date === 'string') {
    const [y, m, d] = date.split('-');
    year = Number(y);
    month = m;
    day = d;
  } else {
    year = date.getFullYear();
    month = String(date.getMonth() + 1).padStart(2, '0');
    day = String(date.getDate()).padStart(2, '0');
  }

  return `${month}/${day}/${year}`;
}

export function formatDateForDisplay(
  date: string | Date | null | undefined,
): string {
  const parts = getDateParts(date);
  if (!parts) {
    return '';
  }

  return `${parts.month}-${parts.day}-${parts.year}`;
}

export function getLocalTodayDateKey(): number {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return Number(`${year}${month}${day}`);
}

function getDateParts(
  date: string | Date | null | undefined,
): { year: string; month: string; day: string } | null {
  if (!date) {
    return null;
  }

  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return {
      year: String(date.getFullYear()),
      month: String(date.getMonth() + 1).padStart(2, '0'),
      day: String(date.getDate()).padStart(2, '0'),
    };
  }

  const normalizedDate = date.trim();
  const ymdMatch = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    return { year, month, day };
  }

  const mdyMatch = normalizedDate.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (mdyMatch) {
    const [, month, day, year] = mdyMatch;
    return { year, month, day };
  }

  return null;
}

function getDateOnlyParts(
  date: string | Date | null | undefined,
): { year: string; month: string; day: string } | null {
  if (!date) {
    return null;
  }

  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return {
      year: String(date.getFullYear()),
      month: String(date.getMonth() + 1).padStart(2, '0'),
      day: String(date.getDate()).padStart(2, '0'),
    };
  }

  const normalizedDate = date.trim();
  const ymdMatch = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    return { year, month, day };
  }

  const isoDateTimeMatch = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (isoDateTimeMatch) {
    const [, year, month, day] = isoDateTimeMatch;
    return { year, month, day };
  }

  const parsed = new Date(normalizedDate);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return {
    year: String(parsed.getFullYear()),
    month: String(parsed.getMonth() + 1).padStart(2, '0'),
    day: String(parsed.getDate()).padStart(2, '0'),
  };
}
